import { useCallback } from 'react';

import useEvent from '@lyearn/core/hooks/useEvent';
import { setIn } from '@lyearn/core/utils/immutable';
import { useTranslation } from '@lyearn/i18n';
import { useSnackbar } from '@lyearn/ui';
import useListWithTempId from '@/hooks/useList/useListWithTempId';
import { OnDropParams } from '@/modules/corporate/components/DraggableList/types';
import { reorderItems } from '@/modules/corporate/components/DraggableList/utils';
import { QuestionEditorProps } from '@/modules/corporate/modules/questions/fields/types';

import { MtfElementsState } from '../../types';
import { getEmptyMtfElement } from '../../utils';
import { adaptMtfElements, adaptMtfElementsToInput } from '../../utils/adapters';
import { MtfComponentActionEnum, onMtfComponentAction } from './actions';

const useMtfComponentAction = ({ question, onChange }: QuestionEditorProps) => {
  const { t } = useTranslation();
  const { showErrorSnackbar } = useSnackbar();

  const onListItemsChange = useCallback(
    (mtfElements: MtfElementsState[]) => {
      onChange(setIn(question, 'mtfComponent', adaptMtfElementsToInput(mtfElements)));
    },
    [onChange, question],
  );

  const [mtfElements, { push, onDelete, onListInputChange, setListItems }] =
    useListWithTempId<MtfElementsState>(adaptMtfElements(question.mtfComponent), onListItemsChange);

  const onAction: onMtfComponentAction = useEvent((action) => {
    switch (action.type) {
      case MtfComponentActionEnum.AddOption: {
        push(getEmptyMtfElement());
        break;
      }

      case MtfComponentActionEnum.UpdateOption: {
        const { index, leftElement, rightElement } = action.payload;
        const updatedLeftElement =
          leftElement === undefined ? mtfElements[index].leftElement : leftElement;
        const updatedRightElement =
          rightElement === undefined ? mtfElements[index].rightElement : rightElement;

        onListInputChange(
          { leftElement: updatedLeftElement, rightElement: updatedRightElement },
          index,
        );
        break;
      }

      case MtfComponentActionEnum.RemoveOption: {
        if (mtfElements.length <= 2) {
          showErrorSnackbar({ message: t('QuizCreator:Validations.OptionLengthError') });
          return;
        }
        onDelete(action.payload.index);
        break;
      }

      case MtfComponentActionEnum.ReorderOption: {
        const { fromIndex, toIndex } = action.payload;
        setListItems(reorderItems({ fromIndex, toIndex, items: mtfElements }));
        break;
      }

      default: {
        break;
      }
    }
  });

  const onAddOption = useCallback(() => {
    onAction({ type: MtfComponentActionEnum.AddOption });
  }, [onAction]);

  const onDrop = useCallback(
    ({ fromIndex, toIndex }: OnDropParams) => {
      onAction({
        type: MtfComponentActionEnum.ReorderOption,
        payload: { fromIndex, toIndex },
      });
    },
    [onAction],
  );

  return { mtfElements, onAction, onAddOption, onDrop };
};

export default useMtfComponentAction;
